import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { useAuthz } from '~/context/AuthzContext';
import { formatDate } from '~/helper/formatDate';
import { formatPrice } from '~/helper/formatPrice';
import { formatWeekday } from '~/helper/formatWeedDate';
import { useTranslate } from '@tolgee/react';
import { Mail, Phone, FileText, User2, DollarSign, Award } from 'lucide-react';
import { clsx } from 'clsx';

interface Props {
  readonly title: string;
  readonly infoText?: string;
  readonly data: {
    readonly date: string;
    readonly offerPrice: string;
    readonly person: string;
    readonly message?: string;
    readonly personEmail?: string;
    readonly personPhone?: string;
    readonly personAddress?: string;
    readonly personPhotoUrl?: string;
    readonly offerConfirmationSecureAssetsPath?: string;
  }[];
  readonly secureAssetsUrl: string;
}

export function CockpitOffersInDetailWidget(props: Props) {
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const showFile = atLeast('partner');
  const showDetails = atLeast('partner');

  // Fixed: Complete JSX structure with timeline grouping by date, maxPrice calculation, sort by price/date, and no placeholders
  // Data is { dateKey, items: [] } from backend, but if flat, group here
  const isGroupedData =
    props.data[0] &&
    typeof props.data[0] === 'object' &&
    'dateKey' in props.data[0];
  const rawData = isGroupedData
    ? props.data.flatMap((group) => group.items)
    : props.data;

  // Filter out invalid offers (null, '0', or parses to 0) before processing
  const validData = rawData.filter((row) => {
    if (!row.offerPrice || row.offerPrice === '0') return false;
    const parsed =
      typeof row.offerPrice === 'string'
        ? parseFloat(row.offerPrice.replace(/[^0-9.]/g, '')) || 0
        : Number(row.offerPrice) || 0;
    return parsed > 0;
  });

  if (validData.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t(props.title)}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            <DollarSign className="h-8 w-8 mx-auto mb-2 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
            <p>{t('cockpit_offers_no_offers')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate max price across all items for badge logic (using valid data)
  const maxPrice = Math.max(
    ...rawData.map((row) => {
      const parsed =
        typeof row.offerPrice === 'string' && row.offerPrice
          ? parseFloat(row.offerPrice.replace(/[^0-9.]/g, '')) || 0
          : typeof row.offerPrice === 'number'
          ? row.offerPrice
          : 0;
      return parsed > 0 ? parsed : -Infinity; // Exclude 0/null for max
    }),
    0,
  );

  // Group by date if not already grouped (using valid data)
  const groupedData = validData.reduce<Record<string, any[]>>((acc, item) => {
    const dateKey = item.date
      ? item.date.split('T')[0]
      : new Date().toISOString().split('T')[0];
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(item);
    return acc;
  }, {});

  // Sort groups by date descending (using valid data)
  const sortedGroups = Object.entries(groupedData).sort(
    ([a], [b]) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t(props.title)}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {validData.length}{' '}
              {validData.length === 1
                ? t('cockpit_offers_angebot_singular')
                : t('cockpit_offers_angebot_plural')}
            </span>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-primary" />
            </div>
          </div>
        </div>
        {props.infoText && (
          <p className="text-sm text-muted-foreground">
            {t(props.infoText as any) || props.infoText}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0">
        <div className="space-y-4 flex-1">
          {sortedGroups.length > 0 &&
            sortedGroups.map(
              ([
                dateKey,
                groupItems,
              ]) => {
                const date = new Date(dateKey);
                const sortedItems = [...groupItems].sort((a, b) => {
                  const aPrice = a.offerPrice
                    ? a.offerPrice.replace(/,/g, '')
                    : '';
                  const bPrice = b.offerPrice
                    ? b.offerPrice.replace(/,/g, '')
                    : '';
                  const pa = parseFloat(aPrice) || 0;
                  const pb = parseFloat(bPrice) || 0;
                  return pb - pa;
                });

                return (
                  <div
                    key={dateKey}
                    className="border-l-2 border-gray-200 dark:border-gray-700 pl-4 relative"
                  >
                    <div className="absolute -left-2 top-1 h-4 w-4 rounded-full bg-white dark:bg-gray-800 border-2 border-primary-400" />
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {formatWeekday(date)}, {formatDate(date)}
                      </h3>{' '}
                      <p className="text-xs text-muted-foreground">
                        {groupItems.length}{' '}
                        {groupItems.length === 1
                          ? t('cockpit_offers_angebot_singular')
                          : t('cockpit_offers_angebot_plural')}
                      </p>
                    </div>
                    <div className="space-y-3">
                      {sortedItems.map((row, index) => {
                        const priceStr =
                          typeof row.offerPrice === 'string'
                            ? row.offerPrice.replace(/,/g, '')
                            : '';
                        const parsed = parseFloat(priceStr) || 0;
                        const isHighest = parsed === maxPrice && parsed > 0;

                        return (
                          <div
                            key={index}
                            className="group hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:bg-gray-800/50 dark:hover:bg-gray-800/50 p-3 rounded-lg transition-colors duration-200 -mx-3"
                          >
                            <div className="flex items-start gap-3">
                              {/* Price Section */}
                              <div
                                className={clsx(
                                  'h-10 w-10 rounded-lg flex items-center justify-center',
                                  isHighest
                                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                                    : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                                )}
                              >
                                {isHighest ? (
                                  <Award className="h-4 w-4" />
                                ) : (
                                  <DollarSign className="h-4 w-4" />
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-base md:text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                                      {row.offerPrice && row.offerPrice !== '0'
                                        ? formatPrice(row.offerPrice)
                                        : t(
                                            'cockpit_offers_price_not_specified',
                                          )}
                                    </span>
                                    {isHighest && (
                                      <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
                                        {t('cockpit_offers_highest')}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Person Info */}
                                <div className="flex items-center gap-2 mt-1">
                                  {row.personPhotoUrl ? (
                                    <img
                                      src={row.personPhotoUrl}
                                      alt="Contact"
                                      className="h-6 w-6 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                      <User2 className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                                    </div>
                                  )}
                                  <span className="text-sm text-gray-700 dark:text-gray-200 dark:text-gray-300 font-medium">
                                    {row.person || t('cockpit_offers_unknown')}
                                  </span>
                                </div>

                                {/* Contact Details & Document */}
                                <div className="flex items-center justify-between mt-2">
                                  {showDetails && (
                                    <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                                      {row.personEmail && (
                                        <a
                                          href={`mailto:${row.personEmail}`}
                                          className="inline-flex items-center gap-1 hover:text-primary-600"
                                        >
                                          <Mail className="h-3 w-3" />
                                          {row.personEmail}
                                        </a>
                                      )}
                                      {row.personPhone && (
                                        <a
                                          href={`tel:${row.personPhone}`}
                                          className="inline-flex items-center gap-1 hover:text-primary-600"
                                        >
                                          <Phone className="h-3 w-3" />
                                          {row.personPhone}
                                        </a>
                                      )}
                                    </div>
                                  )}

                                  {showFile &&
                                    row.offerConfirmationSecureAssetsPath && (
                                      <a
                                        href={`${props.secureAssetsUrl}${row.offerConfirmationSecureAssetsPath}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                                      >
                                        <FileText className="h-3 w-3" />
                                        {t('cockpit_offers_document')}
                                      </a>
                                    )}
                                </div>

                                {/* Message */}
                                {row.message && (
                                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic">
                                    "{row.message}"
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              },
            )}
        </div>
      </CardContent>
    </Card>
  );
}
